/**
 * A dropped folder, read back into files that remember where they came from.
 *
 * `DataTransfer.files` is flat, and every File in it has an empty
 * `webkitRelativePath` — the folder name and the nesting are simply not
 * there. Dropping a folder onto the Drive therefore emptied it out: the
 * contents arrived, the folder itself did not, and someone had to recreate
 * it by hand and move everything into it.
 *
 * The structure is only available through `webkitGetAsEntry`, so that is what
 * is used here. It is a non-standard API and always has been, and it is also
 * the one every file manager on the web is built on.
 */

/** A File that knows the path it had inside the dropped folder. */
export type PathedFile = File & { readonly relativePath?: string };

function fileOf(entry: FileSystemFileEntry): Promise<File | null> {
  return new Promise((resolve) => {
    entry.file(
      (file) => resolve(file),
      // A file that cannot be read is skipped rather than failing the drop:
      // one unreadable item should not cost the other two hundred.
      () => resolve(null),
    );
  });
}

/**
 * Everything in a directory.
 *
 * `readEntries` returns a batch and must be called again until it returns an
 * empty one — the batch size is unspecified and is 100 in Chrome. Reading it
 * once is the reason folder uploads elsewhere silently stop at a hundred
 * files, and is not a bug this Drive is going to have.
 */
function entriesOf(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve) => {
    const all: FileSystemEntry[] = [];

    const readBatch = () => {
      reader.readEntries(
        (batch) => {
          if (batch.length === 0) {
            resolve(all);
            return;
          }
          all.push(...batch);
          readBatch();
        },
        () => resolve(all),
      );
    };

    readBatch();
  });
}

async function walk(entry: FileSystemEntry, prefix: string, into: PathedFile[]): Promise<void> {
  const path = prefix === '' ? entry.name : `${prefix}/${entry.name}`;

  if (entry.isFile) {
    const file = await fileOf(entry as FileSystemFileEntry);
    if (file) {
      // `webkitRelativePath` is read-only, so the path travels beside it.
      Object.defineProperty(file, 'relativePath', { value: path, enumerable: true });
      into.push(file as PathedFile);
    }
    return;
  }

  if (!entry.isDirectory) return;

  const children = await entriesOf((entry as FileSystemDirectoryEntry).createReader());
  // Depth first, in order, so the upload queue reads the way the folder does.
  for (const child of children) await walk(child, path, into);
}

/**
 * The files in a drop, with their paths.
 *
 * The entries must be taken from the DataTransfer synchronously — the items
 * are emptied as soon as the drop handler returns, and awaiting anything
 * first leaves nothing to read. So this collects the entries first and only
 * then walks them.
 *
 * Falls back to the flat file list when the browser has no entry API, which
 * loses the folder but keeps the files, exactly as before.
 */
export async function filesFromDrop(transfer: DataTransfer): Promise<PathedFile[]> {
  const items = Array.from(transfer.items ?? []);

  const entries = items
    .filter((item) => item.kind === 'file')
    .map((item) => {
      const withEntry = item as DataTransferItem & {
        webkitGetAsEntry?: () => FileSystemEntry | null;
      };
      return typeof withEntry.webkitGetAsEntry === 'function' ? withEntry.webkitGetAsEntry() : null;
    })
    .filter((entry): entry is FileSystemEntry => entry !== null);

  if (entries.length === 0) return Array.from(transfer.files) as PathedFile[];

  const files: PathedFile[] = [];
  for (const entry of entries) await walk(entry, '', files);

  // A drop the entry API reported as empty — every item unreadable — should
  // still upload whatever the flat list has rather than appear to do nothing.
  return files.length > 0 ? files : (Array.from(transfer.files) as PathedFile[]);
}

/** Where a file sits inside the drop, however it was chosen. */
export function pathOf(file: File): string {
  const pathed = file as PathedFile & { webkitRelativePath?: string };
  return pathed.relativePath ?? pathed.webkitRelativePath ?? '';
}
